import { DEFAULT_GALLERY_SORT, normalizeGallerySort } from "@/lib/gallerySort";
import { normalizeGallerySearch } from "@/lib/gallerySearch";

const GALLERY_FALLBACK = "/gallery";

export type GalleryCustomizeSource =
  | "gallery_grid"
  | "gallery_detail"
  | "home_gallery"
  | "mini_gallery";

export interface GalleryCustomizeDesign {
  id: string;
  prompt?: string | null;
  title?: string | null;
  image_url?: string | null;
  original_image_path?: string | null;
  back_image_url?: string | null;
  back_image_path?: string | null;
  preset_type?: string | null;
  color?: string | null;
  dress_length?: string | null;
  sleeve_style?: string | null;
  neckline_style?: string | null;
  waistline_style?: string | null;
  silhouette_fit?: string | null;
  fabric_texture?: string | null;
  back_prompt?: string | null;
}

/**
 * Returns a safe in-app path for the gallery listing "back" link.
 * Only allows paths under `/gallery` that are not dress detail URLs, so
 * `from` cannot be used as an open redirect.
 */
export function sanitizeGalleryReturnPath(
  from: string | null | undefined
): string {
  if (from == null || typeof from !== "string") {
    return GALLERY_FALLBACK;
  }

  let decoded = from.trim();
  try {
    decoded = decodeURIComponent(decoded);
  } catch {
    return GALLERY_FALLBACK;
  }

  let parsed: URL;
  try {
    parsed = new URL(decoded, "https://build-a-dress.internal");
  } catch {
    return GALLERY_FALLBACK;
  }

  const pathname = parsed.pathname;

  if (!pathname.startsWith("/gallery")) {
    return GALLERY_FALLBACK;
  }
  if (pathname.startsWith("/gallery/dress/")) {
    return GALLERY_FALLBACK;
  }
  if (pathname.includes("//")) {
    return GALLERY_FALLBACK;
  }
  const allowedParams = new URLSearchParams();
  const sort = normalizeGallerySort(parsed.searchParams.get("sort"));
  const search = normalizeGallerySearch(parsed.searchParams.get("q"));
  if (sort !== DEFAULT_GALLERY_SORT) allowedParams.set("sort", sort);
  if (search) allowedParams.set("q", search);
  const query = allowedParams.toString();
  return `${pathname || GALLERY_FALLBACK}${query ? `?${query}` : ""}`;
}

/**
 * Link target for a dress detail page from the current route. When the user is
 * not already on a gallery listing path, `from` defaults to `/gallery`.
 */
export function galleryDressDetailHref(
  dressId: string,
  currentPathname: string
): string {
  const returnPath =
    currentPathname.startsWith("/gallery") &&
    !currentPathname.startsWith("/gallery/dress/")
      ? currentPathname
      : GALLERY_FALLBACK;
  return `/gallery/dress/${dressId}?from=${encodeURIComponent(returnPath)}`;
}

/**
 * Opens a gallery dress as an editable design brief. This deliberately keeps
 * the source ticket id for attribution while marking the navigation as a
 * customization, so create restores the images/specs without duplicating the
 * public ticket or requiring an account first.
 */
export function galleryCustomizeHref(
  dress: GalleryCustomizeDesign,
  source: GalleryCustomizeSource
): string {
  const designData = {
    id: dress.id,
    title: dress.title ?? null,
    prompt: dress.prompt ?? "",
    image_url: dress.image_url ?? "",
    original_image_path: dress.original_image_path ?? dress.image_url ?? "",
    back_image_url: dress.back_image_url ?? "",
    back_image_path: dress.back_image_path ?? "",
    preset_type: dress.preset_type ?? null,
    color: dress.color ?? "",
    length: dress.dress_length ?? "",
    sleeve: dress.sleeve_style ?? "",
    neckline: dress.neckline_style ?? "",
    waistline: dress.waistline_style ?? "",
    silhouette: dress.silhouette_fit ?? "",
    fabric: dress.fabric_texture ?? "",
    back_prompt: dress.back_prompt ?? "",
  };
  const params = new URLSearchParams({
    gallery_action: "customize",
    gallery_source: source,
    gallery_design: JSON.stringify(designData),
  });
  return `/create?${params.toString()}`;
}
